import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useUsage } from '../../../contexts/UsageContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import { BrandKitState } from '../BrandKitCreator';

interface Logo {
  id: string;
  url: string;
  prompt: string;
}

interface Props {
  brandKit: BrandKitState;
  updateBrandKit: (updates: Partial<BrandKitState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const LogoDesignTab: React.FC<Props> = ({ brandKit, updateBrandKit, onNext, onBack }) => {
  const { getToken } = useAuth();
  const { stats, refresh: refreshUsage } = useUsage();
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1024');
  const [logos, setLogos] = useState<Logo[]>([]);
  const [selectedLogo, setSelectedLogo] = useState<Logo | null>(null);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);

  // Auto-fill prompt with business name
  useEffect(() => {
    if (brandKit.businessName && !prompt) {
      setPrompt(`Modern professional logo for ${brandKit.businessName}`);
    }
  }, [brandKit.businessName, prompt]);

  const handleDownload = useCallback(async (url: string, index: number) => {
    try {
      setDownloadingIndex(index);
      
      const urlObj = new URL(url);
      const isExternalUrl = urlObj.hostname !== window.location.hostname && !urlObj.hostname.includes('localhost');
      
      if (isExternalUrl) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${brandKit.businessName.replace(/\s+/g, '-')}-logo-${index + 1}.png`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Logo download started!');
      } else {
        const response = await fetch(url);
        const blob = await response.blob();
        saveAs(blob, `${brandKit.businessName.replace(/\s+/g, '-')}-logo-${index + 1}.png`);
        toast.success('Logo downloaded successfully!');
      }
    } catch (error) {
      console.error('Error downloading logo:', error);
      toast.error('Failed to download logo. Please try again.');
    } finally {
      setDownloadingIndex(null);
    }
  }, [brandKit.businessName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!prompt) {
      toast.error('Please enter a logo description');
      return;
    }
    
    setLoading(true);
    
    try {
      const userId = await getToken();
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/logo`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId,
            prompt, 
            size 
          }),
        }
      );
      
      const data = await response.json();
      
      // Handle both old and new API formats
      if (data.data) {
        const convertedLogos: Logo[] = data.data.map((img: any, idx: number) => ({
          id: `${Date.now()}-${idx}`,
          url: img.url,
          prompt: img.prompt || prompt
        }));
        setLogos(convertedLogos);
      } else if (data.logos) {
        setLogos(data.logos);
      }
      
      await refreshUsage();
      toast.success('Logos generated successfully!');
    } catch (error) {
      console.error('Error generating logos:', error);
      toast.error('Failed to generate logos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLogo = async (logo: Logo) => {
    setSelectedLogo(logo);
    
    // Convert logo URL to data URL for embedding in email signature
    try {
      const response = await fetch(logo.url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        updateBrandKit({
          logoUrl: logo.url,
          logoPrompt: logo.prompt,
          logoDataUrl: dataUrl,
        });
        toast.success('Logo selected!');
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      // Fallback: just use the URL
      updateBrandKit({
        logoUrl: logo.url,
        logoPrompt: logo.prompt,
      });
      toast.success('Logo selected!');
    }
  };

  const handleContinue = () => {
    if (!selectedLogo) {
      toast.error('Please select a logo to continue');
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Design Your Logo</h2>
          <p className="text-gray-600">
            AI-powered logo generation for <span className="font-semibold text-orange-600">{brandKit.businessName}</span>
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 transition"
        >
          ← Back
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe your ideal logo..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            required
          />
          <p className="mt-2 text-sm text-gray-500">
            Tip: Be specific about style (modern, vintage, minimalist), colors, and any symbols you'd like to include.
          </p>
        </div>

        {/* Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo Size
          </label>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="1024">1024×1024 (Standard)</option>
            <option value="1536">1536×1536 (Large)</option>
            <option value="2048">2048×2048 (Extra Large)</option>
          </select>
        </div>

        {/* Generate Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Generating...
            </>
          ) : (
            'Generate Logos (4 Variations)'
          )}
        </button>
      </form>

      {/* Results */}
      {logos.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-900">Generated Logos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {logos.map((logo, index) => (
              <div
                key={logo.id}
                className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition ${
                  selectedLogo?.id === logo.id
                    ? 'border-orange-500 ring-2 ring-orange-200'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
                onClick={() => handleSelectLogo(logo)}
              >
                <div className="aspect-square bg-gray-100">
                  <img
                    src={logo.url}
                    alt={`Logo ${index + 1}`}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-4 bg-white">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">Logo {index + 1}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(logo.url, index);
                        }}
                        disabled={downloadingIndex === index}
                        className="p-2 text-gray-600 hover:text-gray-900 transition disabled:opacity-50"
                        title="Download"
                      >
                        {downloadingIndex === index ? (
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {selectedLogo?.id === logo.id && (
                  <div className="absolute top-4 right-4">
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Continue Button */}
          {selectedLogo && (
            <button
              onClick={handleContinue}
              className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
            >
              Continue to Email Signature
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LogoDesignTab;
